import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { cursorGlassStyle } from "@/components/cursors/agent-cursor-glass";
import { surfaceRoleAttributes } from "@/components/ui/surface-contract";

// Onboarding renders before the user ever sees the theme switcher; it uses
// the always-dark aurora surface with the brand accent, like the agent
// cursor it introduces.
const ONBOARDING_ACCENT = "#30D158";
const onboardingGlass = cursorGlassStyle(ONBOARDING_ACCENT);

interface Props {
  children: ReactNode;
  className?: string;
  withBackdrop?: boolean;
  placement?: "center" | "top";
  "data-testid"?: string;
}

export function OnboardingCard({
  children,
  className,
  withBackdrop = false,
  placement = "center",
  ...props
}: Props) {
  return (
    <div
      className={cn(
        "fixed inset-0 z-[32000] flex justify-center",
        placement === "top"
          ? "pointer-events-none items-start pt-14"
          : "pointer-events-auto items-center",
        withBackdrop && "bg-black/60 backdrop-blur-sm",
      )}
      data-testid={props["data-testid"] ?? "onboarding-overlay"}
      data-has-backdrop={withBackdrop ? "true" : "false"}
      data-interactive={placement === "center" ? "true" : undefined}
      data-placement={placement}
    >
      <div
        className={cn(
          "denker-agent-cursor-bubble",
          "w-full max-w-md mx-4 border",
          "pointer-events-auto p-appkit-xl flex flex-col gap-6",
          className,
        )}
        data-interactive="true"
        style={
          {
            ...onboardingGlass,
            "--agent-aurora-radius": "24px",
          } as CSSProperties
        }
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "onboarding",
        })}
      >
        {children}
      </div>
    </div>
  );
}
